import React, { useState, useRef, useEffect } from 'react'

import { logger } from '../../services/Logger'
import useUserContextRef from '../../services/useUserContextRef'

const log = logger.createChildLogger({ name: 'ChatboxAudioPlayer' })

type ChatboxAudioPlayerProps = {
    audioSrc: string
    isPlaying: boolean
    onPlayPause: (playing: boolean) => void
}

export default function ChatboxAudioPlayer({ audioSrc, isPlaying, onPlayPause }: ChatboxAudioPlayerProps) {
    const [duration, setDuration] = useState<number>(0)
    const [currentTime, setCurrentTime] = useState<number>(0)
    const audioRef = useRef<HTMLAudioElement>(null)

    const { userStateRef } = useUserContextRef()

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const setAudioData = () => {
            setDuration(audio.duration)
        }

        const updateTime = () => {
            setCurrentTime(audio.currentTime)
        }

        const handleEnded = () => {
            onPlayPause(false)
        }

        audio.addEventListener('loadeddata', setAudioData)
        audio.addEventListener('timeupdate', updateTime)
        audio.addEventListener('ended', handleEnded)

        return () => {
            audio.removeEventListener('loadeddata', setAudioData)
            audio.removeEventListener('timeupdate', updateTime)
            audio.removeEventListener('ended', handleEnded)
        }
    }, [])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        const play = () => {
            audio.play().catch((error) => {
                console.error('Error playing audio:', error)
                onPlayPause(false)
            })
        }

        if (isPlaying) {
            let sinkId = userStateRef.current.mediaSelection.audioOutput
            audio
                // @ts-ignore
                .setSinkId(sinkId)
                .then(play)
                .catch((_e: any) => {
                    // fall back to using default device if setSinkId failed, and play
                    play()
                })
        } else {
            audio.pause()
        }
    }, [isPlaying, onPlayPause])

    const handleScrub = (scrubTime: number): void => {
        const audio = audioRef.current
        if (!audio) return

        setCurrentTime(scrubTime)
        audio.currentTime = scrubTime
    }

    return (
        <div className="mx-3">
            <audio ref={audioRef} src={audioSrc} preload="metadata" />
            <div className="flex items-center w-full items-center gap-x-2">
                <div className="text-xs font-mono text-white">{formatTime(currentTime)}</div>
                <AudioScrubber currentTime={currentTime} duration={duration} onScrub={handleScrub} />
                <div className="text-xs font-mono text-right text-white">{formatTime(duration)}</div>
            </div>
        </div>
    )
}

type AudioScrubberProps = {
    currentTime: number
    duration: number
    onScrub: (scrubTime: number) => void
}

function AudioScrubber({ currentTime, duration, onScrub }: AudioScrubberProps) {
    const progressBarRef = useRef<HTMLDivElement>(null)

    const progressPercentage = duration ? (currentTime / duration) * 100 : 0

    const handleScrub = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!progressBarRef.current) return

        const progressBar = progressBarRef.current
        const bounds = progressBar.getBoundingClientRect()
        const x = e.clientX - bounds.left
        const width = bounds.width

        const percentage = Math.min(Math.max(x / width, 0), 1)
        const scrubTime = percentage * duration

        onScrub(scrubTime)
    }

    return (
        <div
            ref={progressBarRef}
            className="relative grow h-1 cursor-pointer"
            style={{ backgroundColor: 'rgba(100, 100, 100, 0.3)' }}
            onMouseDown={handleScrub}
        >
            <div
                className="absolute top-0 left-0 h-full"
                style={{
                    width: `${progressPercentage}%`,
                    backgroundColor: '#f9cc04',
                }}
            />
        </div>
    )
}

function formatTime(time: number): string {
    if (isNaN(time)) return '00:00'
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}
